import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Trophy, Target, TrendingUp, LogOut } from "lucide-react";
import { DancingCrew } from "@/components/totem/DancingCrew";
import { Confetti, GoalBanner } from "@/components/totem/Confetti";
import { getAudioCtx } from "@/components/totem/discoBeat";

const eur = (n) => `€ ${Number(n || 0).toLocaleString("it-IT", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
const MONTHS_IT = ["Gennaio", "Febbraio", "Marzo", "Aprile", "Maggio", "Giugno", "Luglio", "Agosto", "Settembre", "Ottobre", "Novembre", "Dicembre"];

export default function TotemPage() {
  const navigate = useNavigate();
  const { logout, settings, user } = useAuth();
  const brand = settings?.brand_name || "EVOSOFT";
  const logo = settings?.logo_data_url;

  // Impostazioni Totem personalizzate dall'admin sull'utenza totem.
  const selectedTechIds = useMemo(() => {
    const arr = Array.isArray(user?.totem_tech_ids) ? user.totem_tech_ids : [];
    return new Set(arr.filter(Boolean));
  }, [user]);

  const [slide, setSlide] = useState(0);
  const [ranking, setRanking] = useState([]);
  const [prod, setProd] = useState(null);
  const now = new Date();
  const [year] = useState(now.getFullYear());
  const [month] = useState(now.getMonth() + 1);

  // Obiettivo di Gruppo preso da "Produzione Obiettivo" (BEP); ripiego sul valore dell'account Totem.
  const groupTarget = (Number(prod?.obiettivo_produzione) || 0) || (Number(user?.totem_group_target) || 0);

  const loadAll = async () => {
    try {
      const [r1, r2] = await Promise.all([
        api.get("/dashboard/ranking"),
        api.get("/production-targets", { params: { year, month } }),
      ]);
      setRanking(r1.data?.ranking || []);
      setProd(r2.data);
    } catch {}
  };

  useEffect(() => { loadAll(); const iv = setInterval(loadAll, 30000); return () => clearInterval(iv); /* eslint-disable-next-line */ }, [year, month]);

  const [musicUrl, setMusicUrl] = useState(null);
  useEffect(() => {
    let url = null;
    let version = null;
    const syncMusic = async () => {
      try {
        const m = (await api.get("/totem/music/info")).data?.music;
        const v = m ? m.uploaded_at : "";
        if (v === version) return;
        let next = null;
        if (m) next = URL.createObjectURL((await api.get("/totem/music", { responseType: "blob" })).data);
        if (url) URL.revokeObjectURL(url);
        url = next;
        version = v;
        setMusicUrl(next);
      } catch {}
    };
    syncMusic();
    const iv = setInterval(syncMusic, 60000);
    // Un qualsiasi tocco sul Totem sblocca l'audio per tutta la sessione
    const unlock = () => { const c = getAudioCtx(); if (c && c.state !== "running") c.resume().catch(() => {}); };
    window.addEventListener("pointerdown", unlock);
    window.addEventListener("keydown", unlock);
    return () => {
      clearInterval(iv);
      window.removeEventListener("pointerdown", unlock);
      window.removeEventListener("keydown", unlock);
      if (url) URL.revokeObjectURL(url);
    };
  }, []);

  const doLogout = async () => {
    try { await logout(); } catch {}
    navigate("/login");
  };

  const rankingAll = useMemo(() =>
    [...ranking]
      .filter((r) => (r.role || "tecnico") !== "subappalto")
      .filter((r) => selectedTechIds.size === 0 || selectedTechIds.has(r.technician_id))
      .sort((a, b) => (b.total || 0) - (a.total || 0)),
    [ranking, selectedTechIds]
  );

  const groupTotal = useMemo(() => rankingAll.reduce((s, r) => s + (r.total || 0), 0), [rankingAll]);
  const groupRemaining = Math.max(0, groupTarget - groupTotal);
  const groupPct = groupTarget > 0 ? Math.min(100, (groupTotal / groupTarget) * 100) : 0;
  const hasGroupTarget = groupTarget > 0;

  const rankingMax = useMemo(() => (rankingAll.length ? Math.max(...rankingAll.map((r) => r.total || 0), 1) : 1), [rankingAll]);

  const techSlides = useMemo(() => {
    const collMap = {};
    for (const c of (prod?.collaboratori || [])) if (c.user_id) collMap[c.user_id] = c;
    // Obiettivo del tecnico preso dalla scheda "Tecnici & Utenti" (users.monthly_goal).
    const dp = prod?.daily_progress || {};
    const sortedDaysDesc = Object.keys(dp).sort().reverse();
    return rankingAll.map((r) => {
      const target = Number(r.monthly_goal) || 0;
      const pct = target > 0 ? ((r.total || 0) / target * 100) : 0;
      // Ultimo giorno con produzione del tecnico
      let lastDay = null;
      let lastDayAmount = 0;
      for (const d of sortedDaysDesc) {
        const amt = Number(dp[d]?.per_tecnico?.[r.technician_id] || 0);
        if (amt > 0) {
          lastDay = d;
          lastDayAmount = amt;
          break;
        }
      }
      return {
        user_id: r.technician_id,
        name: r.technician_name,
        role: r.role || "tecnico",
        isCollab: !!collMap[r.technician_id],
        totalMonth: r.total || 0,
        count: r.count || 0,
        target,
        pct,
        hasTarget: target > 0,
        lastDay,
        lastDayAmount,
      };
    });
  }, [rankingAll, prod]);

  // Sequenza slide:
  //   0        = Logo aziendale (10s)
  //   1..5     = Immagini team Evolve Impianti (5s ciascuna)
  //   6        = Ranking Tecnici (30s)
  //   7        = Obiettivo di Gruppo (20s) — solo se admin ha configurato un target
  //   8+       = Performance individuali (5s ciascuna)
  const SEASON = month === 12 || month <= 2 ? "winter" : month <= 5 ? "spring" : month <= 8 ? "summer" : "autumn";
  const TEAM_IMAGES = [1, 2, 3, 4, 5].map((n) => (SEASON === "summer" ? `/totem/evolve_${n}.png` : `/totem/evolve_${n}_${SEASON}.jpg`));
  const LOGO_SLIDE = 0;
  const TEAM_START = 1; // 1..5 (5 immagini)
  const RANKING_SLIDE = 1 + TEAM_IMAGES.length; // 6
  const GROUP_SLIDE = hasGroupTarget ? RANKING_SLIDE + 1 : -1; // 7 se attivo
  const TECH_START = hasGroupTarget ? RANKING_SLIDE + 2 : RANKING_SLIDE + 1;
  const totalSlides = TECH_START + techSlides.length;

  const slideDurationMs =
    slide === LOGO_SLIDE ? 10000 :
    slide >= TEAM_START && slide < RANKING_SLIDE ? 5000 :
    slide === RANKING_SLIDE ? 30000 :
    slide === GROUP_SLIDE ? (groupRemaining <= 0 ? 35000 : 20000) :
    5000;

  useEffect(() => {
    const iv = setTimeout(() => setSlide((s) => (s + 1) % Math.max(1, totalSlides)), slideDurationMs);
    return () => clearTimeout(iv);
  }, [slide, totalSlides, slideDurationMs]);

  const dailyGrid = useMemo(() => {
    if (!prod) return [];
    const daysInMonth = new Date(year, month, 0).getDate();
    const today = new Date();
    const pad = (n) => String(n).padStart(2, "0");
    const todayStr = `${today.getFullYear()}-${pad(today.getMonth() + 1)}-${pad(today.getDate())}`;
    const dp = prod.daily_progress || {};
    // Solo Personale Sociale: somma il "per_tecnico" ristretto ai tecnici configurati come sociali
    const socialiIds = new Set((prod.tecnici || []).map((t) => t.user_id).filter(Boolean));
    const out = [];
    for (let d = 1; d <= daysInMonth; d++) {
      const iso = `${year}-${pad(month)}-${pad(d)}`;
      const wd = new Date(year, month - 1, d).getDay();
      const per = dp[iso]?.per_tecnico || {};
      let total = 0;
      for (const [uid, val] of Object.entries(per)) {
        if (socialiIds.has(uid)) total += Number(val || 0);
      }
      out.push({ date: iso, day: d, weekday: ["Dom", "Lun", "Mar", "Mer", "Gio", "Ven", "Sab"][wd], isSunday: wd === 0, total, isToday: iso === todayStr, isFuture: iso > todayStr });
    }
    return out;
  }, [prod, year, month]);

  const dailyTarget = prod?.obiettivo_giornaliero_calcolato || 0;
  const bepNum = prod?.obiettivo_produzione || 0;
  const progBep = (prod?.progressivo_sociali || 0) + (prod?.margine_esterni || 0);
  const pctBep = bepNum > 0 ? (progBep / bepNum) * 100 : 0;

  return (
    <div className="relative flex h-screen w-screen flex-col overflow-hidden bg-gradient-to-br from-slate-200 via-slate-100 to-slate-300 text-slate-900">
      {/* Logo watermark di sfondo */}
      {logo && (
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 z-0"
          style={{
            backgroundImage: `url(${logo})`,
            backgroundRepeat: "no-repeat",
            backgroundPosition: "center center",
            backgroundSize: "min(60vw, 900px) auto",
            opacity: 0.12,
            filter: "grayscale(20%)",
          }}
        />
      )}

      {/* Header — responsive: si adatta a qualsiasi schermo, "Esci" sempre visibile */}
      <div className="relative z-10 flex flex-wrap items-center justify-between gap-3 border-b border-slate-300 bg-white/70 px-4 py-3 backdrop-blur sm:px-6 md:px-8">
        <div className="flex min-w-0 flex-1 items-center gap-3">
          {logo && <img src={logo} alt={brand} className="h-8 w-8 flex-shrink-0 rounded-sm object-contain sm:h-10 sm:w-10" />}
          <div className="min-w-0 flex-1">
            <div className="truncate text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500 sm:text-[11px]">{brand} · Totem</div>
            <div className="truncate text-lg font-bold tracking-tight text-slate-900 sm:text-xl md:text-2xl">
              {slide === LOGO_SLIDE && brand}
              {slide >= TEAM_START && slide < RANKING_SLIDE && "Evolve Impianti SRLS"}
              {slide === RANKING_SLIDE && "Ranking Tecnici"}
              {slide === GROUP_SLIDE && "Obiettivo di Gruppo"}
              {slide >= TECH_START && techSlides[slide - TECH_START] && `Performance · ${techSlides[slide - TECH_START].name}`}
            </div>
          </div>
        </div>
        <div className="flex flex-shrink-0 items-center gap-2 sm:gap-3">
          <div className="hidden max-w-[40vw] flex-wrap items-center gap-1.5 overflow-hidden md:flex">
            {Array.from({ length: totalSlides }).map((_, i) => (
              <button
                key={i}
                onClick={() => setSlide(i)}
                className={`h-2 rounded-full transition-all sm:h-2.5 ${slide === i ? "w-8 bg-[#0052FF] sm:w-10" : "w-4 bg-slate-400 hover:bg-slate-500 sm:w-6"}`}
                data-testid={`totem-slide-dot-${i}`}
                aria-label={`Slide ${i + 1}`}
              />
            ))}
          </div>
          <div className="hidden font-mono text-base text-slate-700 sm:block sm:text-lg">{new Date().toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" })}</div>
          <button
            onClick={doLogout}
            className="inline-flex flex-shrink-0 items-center gap-1.5 rounded-sm border border-slate-400 bg-white px-2.5 py-1.5 text-xs font-semibold text-slate-700 shadow-sm transition hover:border-red-400 hover:bg-red-50 hover:text-red-700 sm:gap-2 sm:px-3 sm:py-2 sm:text-sm"
            data-testid="totem-logout-btn"
            title="Esci dal Totem"
          >
            <LogOut className="h-3.5 w-3.5 sm:h-4 sm:w-4" /> <span>Esci</span>
          </button>
        </div>
      </div>

      {/* SLIDE 0: Ranking — replica dello stile della pagina Ranking Tecnici */}
      {/* SLIDE 0: Logo aziendale (10s) */}
      {slide === LOGO_SLIDE && (
        <div className="relative z-10 flex flex-1 items-center justify-center p-8">
          <div className="flex flex-col items-center gap-6">
            {logo && (
              <img src={logo} alt={brand} className="max-h-[45vh] max-w-[70vw] object-contain drop-shadow-2xl" />
            )}
            {/* Nome brand sempre visibile: in produzione alcuni tenant hanno caricato solo l'icona */}
            <div className="text-center">
              <div className="text-6xl font-black tracking-tight text-slate-900 sm:text-7xl md:text-8xl">{brand}</div>
              <div className="mt-2 text-2xl font-medium text-slate-600">{MONTHS_IT[month - 1]} {year}</div>
            </div>
          </div>
        </div>
      )}

      {/* SLIDE 1..5: Immagini team (5s ciascuna) */}
      {slide >= TEAM_START && slide < RANKING_SLIDE && (
        <div className="relative z-10 flex flex-1 items-center justify-center overflow-hidden">
          <img
            src={TEAM_IMAGES[slide - TEAM_START]}
            alt={`Evolve Impianti — ${slide - TEAM_START + 1}`}
            className="h-full w-full object-cover"
          />
          <div className="pointer-events-none absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent p-6 text-white">
            <div className="text-xs font-bold uppercase tracking-widest opacity-80">Evolve Impianti SRLS</div>
            <div className="text-2xl font-bold">Il nostro team al lavoro</div>
          </div>
        </div>
      )}

      {/* SLIDE 6: Ranking Tecnici (30s) */}
      {slide === RANKING_SLIDE && (
        <div className="relative z-10 flex flex-1 flex-col overflow-hidden p-4 sm:p-6 md:p-8">
          <div className="mb-3 flex items-center gap-2">
            <Trophy className="h-5 w-5 text-orange-500" />
            <div className="text-xs font-bold uppercase tracking-widest text-slate-600">Classifica del mese · Produzione lorda</div>
          </div>
          {rankingAll.length === 0 && (
            <div className="rounded-lg border border-dashed border-slate-400 bg-white/70 p-8 text-center text-slate-500">Nessun tecnico</div>
          )}
          {rankingAll.length > 0 && (
            <ol
              className={`flex-1 grid content-start gap-2 pr-1 ${
                rankingAll.length <= 8 ? "grid-cols-1" :
                rankingAll.length <= 20 ? "grid-cols-1 lg:grid-cols-2" :
                "grid-cols-1 lg:grid-cols-2 2xl:grid-cols-3"
              }`}
              data-testid="totem-ranking-list"
            >
              {rankingAll.map((r, idx) => {
                const compact = rankingAll.length > 8;
                const veryCompact = rankingAll.length > 16;
                const isSub = (r.role || "") === "subappalto";
                const badgeCls =
                  idx === 0 ? "bg-orange-500 text-white"
                  : idx === 1 ? "bg-slate-400 text-white"
                  : idx === 2 ? "bg-amber-700 text-white"
                  : "bg-slate-200 text-slate-700";
                const pctBar = (r.total || 0) / rankingMax * 100;
                return (
                  <li
                    key={r.technician_id}
                    className={`rounded-lg border border-slate-200 bg-white/85 shadow-sm backdrop-blur ${veryCompact ? "p-2" : compact ? "p-2.5" : "p-4"}`}
                    data-testid={`totem-rank-${idx}`}
                  >
                    <div className="flex items-center justify-between gap-3">
                      <div className="flex min-w-0 items-center gap-2">
                        <div className={`grid place-items-center rounded-sm font-mono font-bold ${badgeCls} ${veryCompact ? "h-7 w-7 text-xs" : compact ? "h-8 w-8 text-sm" : "h-10 w-10 text-lg"}`}>
                          {idx + 1}
                        </div>
                        <div className="min-w-0">
                          <div className={`truncate font-semibold text-slate-900 ${veryCompact ? "text-sm" : compact ? "text-base" : "text-2xl"}`}>
                            {r.technician_name}
                            {isSub && <span className="ml-1.5 rounded-sm bg-orange-100 px-1 py-0.5 text-[9px] font-bold uppercase text-orange-700">Sub</span>}
                          </div>
                          <div className={`text-slate-500 ${veryCompact ? "text-[10px]" : compact ? "text-xs" : "text-sm"}`}>{r.count} lavorazioni</div>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className={`font-mono font-semibold text-slate-900 ${veryCompact ? "text-base" : compact ? "text-lg" : "text-3xl"}`}>{eur(r.total)}</div>
                        <div className={`flex items-center justify-end gap-1 text-emerald-600 ${veryCompact ? "text-[9px]" : "text-xs"}`}>
                          <TrendingUp className="h-3 w-3" /> Prod. lorda
                        </div>
                      </div>
                    </div>
                    <div className={`overflow-hidden rounded-sm bg-slate-200 ${veryCompact ? "mt-1.5 h-1" : compact ? "mt-2 h-1.5" : "mt-3 h-2"}`}>
                      <div className="h-full bg-[#0052FF] transition-all duration-500" style={{ width: `${pctBar}%` }} />
                    </div>
                  </li>
                );
              })}
            </ol>
          )}
        </div>
      )}

      {/* SLIDE "Produzione Obiettivo" nascosta su richiesta utente (era slide 1) */}

      {/* SLIDE: Obiettivo di Gruppo (solo se configurato dall'admin) */}
      {hasGroupTarget && slide === GROUP_SLIDE && (
        <div className="relative z-10 flex flex-1 items-center justify-center overflow-y-auto p-4 sm:p-6 md:p-8" data-testid="totem-group-slide">
          {groupRemaining <= 0 && <><Confetti /><GoalBanner /><DancingCrew musicUrl={musicUrl} /></>}
          <div className="w-full max-w-5xl space-y-6">
            <div className="text-center">
              <div className="text-xs font-bold uppercase tracking-[0.3em] text-slate-500 sm:text-sm">Obiettivo di Gruppo</div>
              <h2 className="mt-1 text-3xl font-bold text-slate-900 sm:text-4xl md:text-6xl">
                {MONTHS_IT[month - 1]} {year}
              </h2>
              <div className="mt-2 text-sm text-slate-500 sm:text-base md:text-lg">
                {rankingAll.length} tecnic{rankingAll.length === 1 ? "o" : "i"} · {rankingAll.map((r) => r.technician_name).join(" · ")}
              </div>
            </div>

            <div className="rounded-2xl border border-slate-300 bg-white/90 p-6 shadow-sm backdrop-blur sm:p-8 md:p-10">
              <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4 text-center">
                  <div className="text-[11px] font-bold uppercase tracking-wider text-emerald-700">Prodotto</div>
                  <div className="mt-1 font-mono text-2xl font-bold text-emerald-700 sm:text-3xl md:text-5xl" data-testid="group-total">{eur(groupTotal)}</div>
                </div>
                <div className="rounded-xl border border-blue-200 bg-blue-50 p-4 text-center">
                  <div className="text-[11px] font-bold uppercase tracking-wider text-blue-700">Obiettivo</div>
                  <div className="mt-1 font-mono text-2xl font-bold text-blue-700 sm:text-3xl md:text-5xl" data-testid="group-target">{eur(groupTarget)}</div>
                </div>
                <div className={`rounded-xl border p-4 text-center ${groupRemaining <= 0 ? "border-emerald-200 bg-emerald-50" : "border-amber-200 bg-amber-50"}`}>
                  <div className={`text-[11px] font-bold uppercase tracking-wider ${groupRemaining <= 0 ? "text-emerald-700" : "text-amber-700"}`}>
                    {groupRemaining <= 0 ? "Obiettivo raggiunto" : "Manca all'obiettivo"}
                  </div>
                  <div className={`mt-1 font-mono text-2xl font-bold sm:text-3xl md:text-5xl ${groupRemaining <= 0 ? "text-emerald-700" : "text-amber-700"}`} data-testid="group-remaining">
                    {groupRemaining <= 0 ? "✓" : eur(groupRemaining)}
                  </div>
                </div>
              </div>

              {/* Progress bar */}
              <div className="mt-6">
                <div className="flex items-center justify-between text-xs font-semibold text-slate-600 sm:text-sm">
                  <span>0</span>
                  <span className="font-mono text-base text-slate-900 sm:text-xl">{groupPct.toFixed(1)}%</span>
                  <span>{eur(groupTarget)}</span>
                </div>
                <div className="mt-2 h-4 overflow-hidden rounded-sm bg-slate-200 sm:h-5">
                  <div
                    className={`h-full transition-all duration-500 ${groupRemaining <= 0 ? "bg-gradient-to-r from-emerald-500 to-emerald-600" : "bg-gradient-to-r from-blue-500 to-blue-600"}`}
                    style={{ width: `${groupPct}%` }}
                  />
                </div>
              </div>

              {/* Breakdown per tecnico */}
              <div className="mt-6 space-y-2">
                {rankingAll.map((r) => {
                  const share = groupTotal > 0 ? (r.total || 0) / groupTotal * 100 : 0;
                  return (
                    <div key={r.technician_id} className="flex items-center justify-between gap-3 rounded-sm border border-slate-200 bg-slate-50 px-3 py-2 text-sm md:text-base">
                      <div className="min-w-0 flex-1 truncate font-semibold text-slate-800">{r.technician_name}</div>
                      <div className="font-mono font-bold text-slate-900">{eur(r.total || 0)}</div>
                      <div className="w-14 text-right text-xs text-slate-500">{share.toFixed(0)}%</div>
                    </div>
                  );
                })}
                {rankingAll.length === 0 && (
                  <div className="rounded-sm border border-slate-200 bg-slate-50 p-4 text-center text-sm text-slate-500">
                    Nessun tecnico selezionato. Configura la lista dall'anagrafica Tecnici &amp; Utenti.
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* SLIDE 7+ (o 8+ se c'è group): Performance individuali */}
      {slide >= TECH_START && techSlides[slide - TECH_START] && (() => {
        const t = techSlides[slide - TECH_START];
        const daily = t.target && (prod?.working_days || 0) ? t.target / prod.working_days : 0;
        const pct = t.pct || 0;
        const isHit = t.hasTarget && pct >= 100;
        const initial = (t.name || "?").trim().charAt(0).toUpperCase();
        const roleLabel = t.role === "subappalto" ? "Subappalto" : (t.isCollab ? "Collaboratore esterno" : "Personale sociale");
        return (
          <div className="relative z-10 flex flex-1 items-center justify-center overflow-y-auto p-4 sm:p-6 md:p-8">
            <div className="w-full max-w-6xl">
              <div className="flex flex-col items-center gap-4 rounded-2xl border border-slate-300 bg-white/85 p-4 shadow-sm backdrop-blur sm:flex-row sm:gap-6 sm:p-6 md:gap-8 md:p-10">
                <div className={`flex h-20 w-20 flex-shrink-0 items-center justify-center rounded-full text-4xl font-bold text-white shadow-md sm:h-28 sm:w-28 sm:text-5xl md:h-40 md:w-40 md:text-7xl ${isHit ? "bg-gradient-to-br from-emerald-500 to-emerald-700" : "bg-gradient-to-br from-blue-500 to-blue-700"}`}>
                  {initial}
                </div>
                <div className="min-w-0 flex-1 text-center sm:text-left">
                  <div className="truncate text-2xl font-bold text-slate-900 sm:text-3xl md:text-5xl">{t.name}</div>
                  <div className="mt-1 text-sm text-slate-500 sm:mt-2 sm:text-base md:text-xl">{roleLabel} · Performance mensile · {MONTHS_IT[month - 1]} {year}</div>
                  {t.lastDay && (
                    <div className="mt-2 inline-flex items-center gap-2 rounded-sm bg-emerald-50 px-2.5 py-1 text-xs font-semibold text-emerald-700 ring-1 ring-emerald-200 sm:text-sm md:text-base" data-testid={`totem-last-day-${t.user_id}`}>
                      <span className="opacity-80">Ultimo giorno</span>
                      <span className="font-mono">{t.lastDay.split("-").reverse().join("/")}</span>
                      <span>·</span>
                      <span className="font-mono font-bold">{eur(t.lastDayAmount)}</span>
                      <span className="opacity-80">prodotti</span>
                    </div>
                  )}
                </div>
                <div className="text-center sm:text-right">
                  {t.hasTarget ? (
                    <>
                      <div className={`font-mono text-4xl font-bold sm:text-5xl md:text-7xl ${isHit ? "text-emerald-600" : "text-blue-600"}`}>{pct.toFixed(0)}%</div>
                      <div className="text-sm text-slate-500 sm:text-base md:text-lg">del target</div>
                    </>
                  ) : (
                    <>
                      <div className="text-xl font-bold text-slate-400 sm:text-2xl md:text-3xl">Obiettivo</div>
                      <div className="text-xs text-slate-400 sm:text-sm md:text-lg">non impostato</div>
                    </>
                  )}
                </div>
              </div>

              <div className={`mt-4 grid gap-3 sm:mt-6 sm:gap-4 ${t.hasTarget ? "grid-cols-2 md:grid-cols-4" : "grid-cols-2"}`}>
                <div className="rounded-xl border border-slate-300 bg-white/85 p-3 shadow-sm backdrop-blur sm:p-5">
                  <div className="text-[10px] font-bold uppercase tracking-widest text-emerald-700 sm:text-xs">Prodotto</div>
                  <div className="mt-1 font-mono text-xl font-bold text-slate-900 sm:mt-2 sm:text-2xl md:text-4xl">{eur(t.totalMonth)}</div>
                </div>
                {t.hasTarget && (
                  <>
                    <div className="rounded-xl border border-slate-300 bg-white/85 p-3 shadow-sm backdrop-blur sm:p-5">
                      <div className="text-[10px] font-bold uppercase tracking-widest text-blue-700 sm:text-xs">Target Mese</div>
                      <div className="mt-1 font-mono text-xl font-bold text-slate-900 sm:mt-2 sm:text-2xl md:text-4xl">{eur(t.target)}</div>
                    </div>
                    <div className="rounded-xl border border-slate-300 bg-white/85 p-3 shadow-sm backdrop-blur sm:p-5">
                      <div className="text-[10px] font-bold uppercase tracking-widest text-amber-700 sm:text-xs">Target/giorno</div>
                      <div className="mt-1 font-mono text-xl font-bold text-slate-900 sm:mt-2 sm:text-2xl md:text-4xl">{eur(daily)}</div>
                    </div>
                  </>
                )}
                <div className="rounded-xl border border-slate-300 bg-white/85 p-3 shadow-sm backdrop-blur sm:p-5">
                  <div className="text-[10px] font-bold uppercase tracking-widest text-purple-700 sm:text-xs">Lavorazioni</div>
                  <div className="mt-1 font-mono text-xl font-bold text-slate-900 sm:mt-2 sm:text-2xl md:text-4xl">{t.count}</div>
                </div>
              </div>

              {t.hasTarget && (
                <div className="mt-4 sm:mt-8">
                  <div className="mb-2 flex justify-between text-xs text-slate-600 sm:text-sm">
                    <span>0</span>
                    <span>Target: {eur(t.target)}</span>
                  </div>
                  <div className="h-4 w-full overflow-hidden rounded-full bg-slate-300 sm:h-6 md:h-8">
                    <div
                      className={`h-full rounded-full transition-all duration-1000 ${isHit ? "bg-gradient-to-r from-emerald-500 to-emerald-400" : "bg-gradient-to-r from-blue-600 to-blue-400"}`}
                      style={{ width: `${Math.min(pct, 100)}%` }}
                    ></div>
                  </div>
                  {isHit && <div className="mt-2 text-center text-lg font-bold text-emerald-600 sm:mt-3 sm:text-xl md:text-2xl">OBIETTIVO RAGGIUNTO!</div>}
                </div>
              )}
            </div>
          </div>
        );
      })()}
    </div>
  );
}


